/**
 * UiLocatorOverlay — draws the answer to a locateUi() call. Mounted once, in Layout.
 *
 * Two outcomes, because roughly half the features our guidance copy names are
 * conditional on state the reader does not have yet:
 *
 *   element on screen  → scroll it into view and ring it for a few seconds.
 *   element not present → a card naming the click path and the precondition
 *                         ("gold trails need a trace to have run first").
 *
 * The ring is pointer-events-none so the highlighted control stays clickable —
 * the point is to let someone act on what they just found, not just admire it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Crosshair, X } from 'lucide-react';
import { UI_LOCATIONS, getUiLocation, type UiLocation, type UiLocationKey } from './uiLocations';
import { subscribeLocateUi } from './uiLocator';

/** Long enough to look away from the guide and back at the ring. */
const RING_MS = 5000;
/** Longer — the fallback card is text that has to be read. */
const CARD_MS = 9000;
const RING_PAD = 6;

interface Ring {
    top: number;
    left: number;
    width: number;
    height: number;
}

function measure(selector: string): Ring | null {
    let el: HTMLElement | null = null;
    try {
        el = document.querySelector<HTMLElement>(selector);
    } catch {
        return null;
    }
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 2 && r.height < 2) return null;
    return {
        top: r.top - RING_PAD,
        left: r.left - RING_PAD,
        width: r.width + RING_PAD * 2,
        height: r.height + RING_PAD * 2,
    };
}

export const UiLocatorOverlay: React.FC<{ routeKey?: string }> = ({ routeKey }) => {
    const [active, setActive] = useState<{ key: UiLocationKey; location: UiLocation } | null>(null);
    const [ring, setRing] = useState<Ring | null>(null);
    const pendingTimeouts = useRef(new Set<number>());
    const retryFrame = useRef<number | null>(null);
    const requestVersion = useRef(0);

    const cancelPending = useCallback(() => {
        requestVersion.current += 1;
        if (retryFrame.current !== null) {
            window.cancelAnimationFrame(retryFrame.current);
            retryFrame.current = null;
        }
        pendingTimeouts.current.forEach(id => window.clearTimeout(id));
        pendingTimeouts.current.clear();
    }, []);

    const schedule = useCallback((callback: () => void, delay: number, version: number) => {
        let id = 0;
        id = window.setTimeout(() => {
            pendingTimeouts.current.delete(id);
            if (requestVersion.current !== version) return;
            callback();
        }, delay);
        pendingTimeouts.current.add(id);
        return id;
    }, []);

    const dismiss = useCallback(() => {
        cancelPending();
        setActive(null);
        setRing(null);
    }, [cancelPending]);

    useEffect(() => subscribeLocateUi((key) => {
        // The request arrives over a window event, so the key is untrusted.
        if (!(key in UI_LOCATIONS)) return;
        cancelPending();
        const version = requestVersion.current;
        const location = getUiLocation(key);
        setActive({ key, location });

        const ringPresent = (found: Ring) => {
            try {
                document.querySelector(location.selector)?.scrollIntoView({
                    block: 'center',
                    inline: 'center',
                    behavior: 'smooth',
                });
            } catch { /* selector already validated by measure() */ }
            setRing(found);
            [80, 200, 400, 700].forEach(ms => schedule(() => {
                setRing(prev => (prev ? measure(location.selector) ?? prev : prev));
            }, ms, version));
            schedule(dismiss, RING_MS, version);
        };

        const found = measure(location.selector);
        if (found) {
            ringPresent(found);
            return;
        }

        // locateUi() may have just opened a buried panel (e.g. Graph Legend).
        // Give React a paint to mount it before falling back to the click-path card.
        retryFrame.current = window.requestAnimationFrame(() => {
            retryFrame.current = null;
            schedule(() => {
                const retry = measure(location.selector);
                if (retry) {
                    ringPresent(retry);
                    return;
                }
                setRing(null);
                schedule(dismiss, CARD_MS, version);
            }, 50, version);
        });
    }), [cancelPending, dismiss, schedule]);

    // Layout stays mounted across React Router transitions. A location key
    // change invalidates any delayed retry or remeasure for the previous page.
    useEffect(() => {
        dismiss();
    }, [dismiss, routeKey]);

    // Keep the ring on its element while the page moves under it.
    useEffect(() => {
        if (!active || !ring) return;
        const reposition = () => setRing(prev => (prev ? measure(active.location.selector) ?? prev : prev));
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [active, ring]);

    useEffect(() => {
        if (!active) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') dismiss();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, dismiss]);

    useEffect(() => () => cancelPending(), [cancelPending]);

    if (!active) return null;
    const { location } = active;

    return createPortal(
        ring ? (
            <div className="fixed inset-0 z-[250] pointer-events-none" data-testid="ui-locator-ring">
                <div
                    className="absolute rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse transition-[top,left,width,height] duration-200"
                    style={{ top: ring.top, left: ring.left, width: ring.width, height: ring.height }}
                />
                <div
                    className="absolute flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-ink text-white text-[11px] font-medium whitespace-nowrap"
                    style={{
                        // Above the ring, unless it is already near the top of the viewport.
                        top: ring.top > 34 ? ring.top - 28 : ring.top + ring.height + 8,
                        left: Math.max(8, Math.min(ring.left, window.innerWidth - 240)),
                    }}
                >
                    <Crosshair className="w-3 h-3 text-primary" aria-hidden />
                    {location.label}
                </div>
            </div>
        ) : (
            <div
                className="fixed top-20 left-1/2 -translate-x-1/2 z-[250] w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-border bg-card shadow-lg pointer-events-auto"
                role="status"
                data-testid="ui-locator-card"
            >
                <div className="flex items-start gap-2 p-3">
                    <Crosshair className="w-4 h-4 mt-0.5 shrink-0 text-primary" aria-hidden />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground">{location.label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{location.where}</p>
                        {location.reveal && (
                            <p className="mt-1.5 text-xs leading-relaxed break-words text-amber-700 dark:text-amber-300">
                                {location.reveal}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="Dismiss"
                        className="shrink-0 p-1 -m-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        ),
        document.body,
    );
};
